/* eslint-disable fp/no-throw */
/* eslint-disable fp/no-mutation */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useReducer,
} from 'react'
import {
  Box,
  Button,
  Paper,
  Typography,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
} from '@mui/material'
import { Warning, CloudUpload, MenuBook, Delete } from '@mui/icons-material'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import axios from 'src/utilities/http-client'
import { TranscriptSegment } from './transcript-segment'
import {
  parseTranscribeJson,
  generateVTT,
  findCurrentWordIndex,
  generateTranscriptJSON,
  isCorrectedFormat,
} from './utils'
import {
  TranscribeResult,
  TranscriptSegment as TranscriptSegmentType,
  SpeakerEditConfirmation,
  ModalState,
} from './types'

interface Props {
  currentTime: number // From your Mux player
  onSeek?: (time: number) => void // Callback to control video playback
  videoId?: string // Video ID to fetch transcript
}

interface VocabularyData {
  phrase: string
  soundsLike: string
  ipa: string
  displayAs: string
}

interface VocabularyItem {
  phrase: string
  soundsLike?: string
  ipa?: string
  displayAs?: string
}

interface VocabularyResponse {
  vocabularyName: string
  vocabularyState: string
  terms: VocabularyItem[]
}

type ModalAction =
  | { type: 'OPEN_ADD_VOCABULARY'; word: string }
  | { type: 'OPEN_VIEW_VOCABULARY' }
  | { type: 'OPEN_SPEAKER_CONFIRMATION'; confirmation: SpeakerEditConfirmation }
  | { type: 'CLOSE_MODAL' }

let modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'OPEN_ADD_VOCABULARY':
      return {
        type: 'addVocabulary',
        data: { word: action.word },
      }
    case 'OPEN_VIEW_VOCABULARY':
      return {
        type: 'viewVocabulary',
      }
    case 'OPEN_SPEAKER_CONFIRMATION':
      return {
        type: 'speakerConfirmation',
        data: { speakerConfirmation: action.confirmation },
      }
    case 'CLOSE_MODAL':
      return {
        type: 'none',
      }
    default:
      return state
  }
}

export const TranscriptEditor: React.FC<Props> = ({
  currentTime,
  onSeek,
  videoId,
}) => {
  let [segments, setSegments] = useState<TranscriptSegmentType[]>([])
  let [editingSegments, setEditingSegments] = useState<Set<string>>(new Set())
  let [editingSpeakerSegments, setEditingSpeakerSegments] = useState<
    Set<string>
  >(new Set())
  let [modalState, modalDispatch] = useReducer(modalReducer, { type: 'none' })
  let [loading, setLoading] = useState(false)
  let [error, setError] = useState<string | null>(null)
  let [isDirty, setIsDirty] = useState(false)
  let [showSaveSuccess, setShowSaveSuccess] = useState(false)
  let [saveInProgress, setSaveInProgress] = useState(false)
  let [saveMessage, setSaveMessage] = useState('File saved successfully')
  let [hasWordTimings, setHasWordTimings] = useState(true)

  // Vocabulary state
  let [vocabularyData, setVocabularyData] = useState<VocabularyData>({
    phrase: '',
    soundsLike: '',
    ipa: '',
    displayAs: '',
  })
  let [vocabularyItems, setVocabularyItems] = useState<VocabularyItem[]>([])
  let [isLoadingVocabulary, setIsLoadingVocabulary] = useState(false)
  let [vocabularyError, setVocabularyError] = useState<string | null>(null)
  let [isSubmittingVocabulary, setIsSubmittingVocabulary] = useState(false)
  let [deletingItems, setDeletingItems] = useState<Set<string>>(new Set())

  // Memoize current word location to prevent unnecessary recalculations
  let currentWordLocation = useMemo(() => {
    if (!hasWordTimings) return null
    return findCurrentWordIndex(segments, currentTime)
  }, [segments, currentTime, hasWordTimings])

  let loadTranscript = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)

    try {
      // Always load the original transcript first to get subtitle-friendly segmentation
      let response = await axios.get(
        `/api/create/video/${id}/transcript/original`,
      )

      if (!response?.data) {
        throw new Error('Invalid response for original transcript')
      }

      let signedUrl = response.data

      // Download the transcript file
      let fileResponse = await axios.get(signedUrl, { responseType: 'text' })
      let text = fileResponse.data

      if (!text) {
        throw new Error('Empty original transcript data')
      }

      let data: TranscribeResult = JSON.parse(text)

      // Check if we have word-level timings
      let hasWords =
        (data.results?.items && data.results.items.length > 0) ?? false
      setHasWordTimings(hasWords)

      // Parse with subtitle-friendly segmentation
      let parsedSegments = parseTranscribeJson(data)
      setSegments(parsedSegments)
      setIsDirty(false)
      setEditingSegments(new Set())
      setLoading(false)
    } catch (originalErr) {
      try {
        // Fall back to corrected transcript if original fails
        let correctedResponse = await axios.get(
          `/api/create/video/${id}/transcript/corrected`,
        )

        // Check if we received a valid response
        if (correctedResponse?.data) {
          let correctedSignedUrl = correctedResponse.data

          // Download the corrected transcript file
          let fileResponse = await axios.get(correctedSignedUrl, {
            responseType: 'text',
          })
          let text = fileResponse.data

          // Verify we got valid data
          if (!text) {
            throw new Error('Empty transcript data')
          }

          // Try parsing the JSON
          let data = JSON.parse(text)

          // Check if we have word-level timings - different for each format
          let hasWords = false
          if (isCorrectedFormat(data)) {
            // For corrected format, check if segments have words arrays
            hasWords = data.segments.some(
              (segment: TranscriptSegmentType) =>
                segment.words && segment.words.length > 0,
            )
          } else {
            // For standard format, check AWS Transcribe items
            hasWords =
              (data.results?.items && data.results.items.length > 0) ?? false
          }
          setHasWordTimings(hasWords)

          let parsedSegments = parseTranscribeJson(data)
          setSegments(parsedSegments)
          setIsDirty(false)
          setEditingSegments(new Set())
          setLoading(false)
          return // Exit early if we successfully loaded the corrected transcript
        }
        throw new Error('Invalid response for corrected transcript')
      } catch (correctedErr) {
        // If both fail, try the regular transcript endpoint
        try {
          let response = await axios.get(`/api/create/video/${id}/transcript`)

          if (!response?.data) {
            throw new Error('Invalid response for transcript')
          }

          let signedUrl = response.data

          // Download the transcript file
          let fileResponse = await axios.get(signedUrl, {
            responseType: 'text',
          })
          let text = fileResponse.data

          if (!text) {
            throw new Error('Empty transcript data')
          }

          let data: TranscribeResult = JSON.parse(text)

          // Check if we have word-level timings
          let hasWords =
            (data.results?.items && data.results.items.length > 0) ?? false
          setHasWordTimings(hasWords)

          let parsedSegments = parseTranscribeJson(data)
          setSegments(parsedSegments)
          setIsDirty(false)
          setEditingSegments(new Set())
        } catch (err) {
          let errorMessage =
            err instanceof Error ? err.message : 'Failed to load transcript'
          setError(errorMessage)
          // eslint-disable-next-line no-console
          console.error(err)
        }
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (videoId) {
      loadTranscript(videoId)
    }
  }, [videoId])

  let handleToggleEdit = useCallback((segmentId: string) => {
    setEditingSegments((prev) => {
      let newSet = new Set(prev)
      if (newSet.has(segmentId)) {
        newSet.delete(segmentId)
      } else {
        // Close all other edits
        newSet.clear()
        newSet.add(segmentId)
      }
      return newSet
    })
  }, [])

  let handleUpdateText = useCallback(
    (segmentId: string, newText: string) => {
      setSegments((prev) =>
        prev.map((segment) => {
          if (segment.id === segmentId) {
            if (!hasWordTimings) {
              // If no word timings, just update the text
              return { ...segment, text: newText }
            }

            // Parse the new text into words while preserving timestamps
            let words = newText
              .split(/\s+/)
              .filter((word) => word.length > 0)
              .map((word, index) => {
                let originalWord = segment.words[index]
                if (originalWord) {
                  return { ...originalWord, content: word }
                }
                // For new words, interpolate timestamps
                let prevWord = segment.words[index - 1]
                let nextWord = segment.words[index + 1]
                return {
                  id: `${Date.now()}-${index}`,
                  content: word,
                  startTime: prevWord ? prevWord.endTime : segment.startTime,
                  endTime: nextWord ? nextWord.startTime : segment.endTime,
                  confidence: 1,
                }
              })

            return { ...segment, text: newText, words }
          }
          return segment
        }),
      )
      setIsDirty(true)
    },
    [hasWordTimings],
  )

  // Function to save transcript to S3 via presigned URLs
  let handleSaveTranscript = useCallback(async () => {
    if (!videoId) return

    setSaveInProgress(true)
    try {
      // Get the presigned URLs from the API
      let response = await axios.get(
        `/api/create/video/${videoId}/transcript/put`,
      )

      if (!response?.data) {
        throw new Error('Invalid response for presigned URLs')
      }

      let { transcriptUploadURL, vttUploadURL } = response.data

      // Generate the transcript JSON and VTT content
      let jsonContent = generateTranscriptJSON(segments)
      let vttContent = generateVTT(segments)

      // Create blobs for upload
      let jsonBlob = new Blob([jsonContent], { type: 'application/json' })
      let vttBlob = new Blob([vttContent], { type: 'text/vtt' })

      // Upload both files to S3
      await Promise.all([
        axios.put(transcriptUploadURL, jsonBlob, {
          headers: {
            'Cache-Control': 'max-age=2592000',
            'content-type': 'application/json',
          },
        }),
        axios.put(vttUploadURL, vttBlob, {
          headers: {
            'Cache-Control': 'max-age=2592000',
            'content-type': 'text/vtt',
          },
        }),
      ])

      setSaveMessage(
        'The transcript has been saved and a subtitle track has been generated for the video.',
      )
      setShowSaveSuccess(true)
      setIsDirty(false)

      // Re-initialize the transcript to load the latest version
      if (videoId) {
        setTimeout(() => {
          // Add a small delay before reloading to ensure S3 consistency
          loadTranscript(videoId)
        }, 1000)
      }
    } catch (err) {
      let errorMessage =
        err instanceof Error ? err.message : 'Failed to save transcript'
      setError(errorMessage)
    } finally {
      setSaveInProgress(false)
    }
  }, [segments, videoId])

  // Add speaker editing functionality
  let handleToggleSpeakerEdit = useCallback((segmentId: string) => {
    setEditingSpeakerSegments((prev) => {
      let newSet = new Set(prev)
      if (newSet.has(segmentId)) {
        newSet.delete(segmentId)
      } else {
        // Close all other edits
        newSet.clear()
        newSet.add(segmentId)
      }
      return newSet
    })
  }, [])

  // Update only the current segment
  let updateSingleSpeaker = useCallback(
    (segmentId: string, newName: string) => {
      setSegments((prev) =>
        prev.map((segment) =>
          segment.id === segmentId ? { ...segment, speaker: newName } : segment,
        ),
      )
      setIsDirty(true)
    },
    [],
  )

  // Handle speaker update with confirmation dialog
  let handleUpdateSpeaker = useCallback(
    (segmentId: string, oldName: string, newName: string) => {
      if (oldName === newName) return

      // Check if there are multiple segments with the same speaker
      let hasSameSpeakerInOtherSegments = segments.some(
        (s) => s.id !== segmentId && s.speaker === oldName,
      )

      if (hasSameSpeakerInOtherSegments) {
        // Open confirmation dialog
        modalDispatch({
          type: 'OPEN_SPEAKER_CONFIRMATION',
          confirmation: { segmentId, oldName, newName },
        })
      } else {
        // Just update this one segment
        updateSingleSpeaker(segmentId, newName)
      }
    },
    [segments],
  )

  // Update all segments with the same speaker name
  let updateAllSpeakers = useCallback((oldName: string, newName: string) => {
    setSegments((prev) =>
      prev.map((segment) =>
        segment.speaker === oldName
          ? { ...segment, speaker: newName }
          : segment,
      ),
    )
    setIsDirty(true)
  }, [])

  // Handle confirmation dialog actions
  let handleSpeakerConfirmationClose = () => {
    modalDispatch({ type: 'CLOSE_MODAL' })
  }

  let handleConfirmSingleUpdate = () => {
    let confirmation = modalState.data?.speakerConfirmation
    if (confirmation) {
      updateSingleSpeaker(confirmation.segmentId, confirmation.newName)
    }
    modalDispatch({ type: 'CLOSE_MODAL' })
  }

  let handleConfirmAllUpdates = () => {
    let confirmation = modalState.data?.speakerConfirmation
    if (confirmation) {
      updateAllSpeakers(confirmation.oldName, confirmation.newName)
    }
    modalDispatch({ type: 'CLOSE_MODAL' })
  }

  // Vocabulary handlers
  let handleOpenAddVocabulary = useCallback((word: string) => {
    setVocabularyData({
      phrase: '',
      soundsLike: '',
      ipa: '',
      displayAs: word,
    })
    modalDispatch({ type: 'OPEN_ADD_VOCABULARY', word })
  }, [])

  let handleOpenViewVocabulary = useCallback(async () => {
    modalDispatch({ type: 'OPEN_VIEW_VOCABULARY' })
    setIsLoadingVocabulary(true)
    setVocabularyError(null)

    try {
      let response = await axios.get('/api/create/video/vocabulary')
      let vocabData = response.data as VocabularyResponse
      setVocabularyItems(vocabData.terms || [])
    } catch (e) {
      let errorMessage =
        e instanceof Error ? e.message : 'Failed to load vocabulary'
      setVocabularyError(errorMessage)
    } finally {
      setIsLoadingVocabulary(false)
    }
  }, [])

  let handleCloseModal = useCallback(() => {
    modalDispatch({ type: 'CLOSE_MODAL' })
    setVocabularyData({ phrase: '', soundsLike: '', ipa: '', displayAs: '' })
    setVocabularyItems([])
    setVocabularyError(null)
  }, [])

  let handleVocabularySubmit = useCallback(async () => {
    if (!vocabularyData.phrase) return

    setIsSubmittingVocabulary(true)
    try {
      await axios.post('/api/create/video/vocabulary', {
        phrase: vocabularyData.phrase,
        soundsLike: vocabularyData.soundsLike || undefined,
        ipa: vocabularyData.ipa || undefined,
        displayAs: vocabularyData.displayAs || undefined,
      })
      handleCloseModal()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to add vocabulary:', e)
    } finally {
      setIsSubmittingVocabulary(false)
    }
  }, [vocabularyData, handleCloseModal])

  let handleDeleteVocabulary = useCallback(async (phrase: string) => {
    setDeletingItems((prev) => new Set([...prev, phrase]))

    try {
      await axios.delete(
        `/api/create/video/vocabulary?phrase=${encodeURIComponent(phrase)}`,
      )

      // Refresh the vocabulary list
      let response = await axios.get('/api/create/video/vocabulary')
      let vocabData = response.data as VocabularyResponse
      setVocabularyItems(vocabData.terms || [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete vocabulary item:', e)
    } finally {
      setDeletingItems((prev) => {
        let newSet = new Set(prev)
        newSet.delete(phrase)
        return newSet
      })
    }
  }, [])

  // Close all edits when clicking outside
  useEffect(() => {
    let handleClickOutside = (e: MouseEvent) => {
      let target = e.target as HTMLElement
      if (!target.closest('.transcript-segment-editing')) {
        setEditingSegments(new Set())
        setEditingSpeakerSegments(new Set())
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Auto-scroll to current segment
  useEffect(() => {
    if (currentWordLocation) {
      let element = document.getElementById(
        `segment-${currentWordLocation.segmentId}`,
      )
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [currentWordLocation?.segmentId])

  return (
    <Paper
      elevation={3}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
      }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Transcript Editor
          </Typography>
          {!hasWordTimings && segments.length > 0 && (
            <Chip
              icon={<Warning />}
              label="No word timings available"
              color="warning"
              size="small"
              sx={{ mr: 2 }}
            />
          )}
          <Button
            startIcon={<MenuBook />}
            onClick={handleOpenViewVocabulary}
            disabled={!videoId}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            View Vocabulary
          </Button>
          <Button
            startIcon={<CloudUpload />}
            onClick={handleSaveTranscript}
            disabled={segments.length === 0 || saveInProgress || !videoId}
            color={isDirty ? 'primary' : undefined}
            variant={isDirty ? 'contained' : 'text'}
            sx={{ mr: 1 }}
          >
            {saveInProgress
              ? 'Saving...'
              : 'Save Transcript and Enable Subtitles'}
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {segments.length === 0 && !loading && (
          <Box display="flex" flexDirection="column" alignItems="center" p={4}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No transcript loaded
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload an AWS Transcribe JSON file to get started
            </Typography>
          </Box>
        )}

        {segments.map((segment) => (
          <div
            key={segment.id}
            id={`segment-${segment.id}`}
            className={
              editingSegments.has(segment.id) ||
              editingSpeakerSegments.has(segment.id)
                ? 'transcript-segment-editing'
                : ''
            }
          >
            <TranscriptSegment
              segment={segment}
              isEditingSpeaker={editingSpeakerSegments.has(segment.id)}
              onUpdateSpeaker={handleUpdateSpeaker}
              isEditing={editingSegments.has(segment.id)}
              currentWordLocation={currentWordLocation}
              onToggleEdit={handleToggleEdit}
              onToggleSpeakerEdit={handleToggleSpeakerEdit}
              onUpdateText={handleUpdateText}
              onSeek={onSeek}
              onOpenAddVocabulary={handleOpenAddVocabulary}
            />
          </div>
        ))}
      </Box>

      <Snackbar
        open={showSaveSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSaveSuccess(false)}
        message={saveMessage}
      />

      {/* Speaker rename confirmation dialog */}
      <Dialog
        open={modalState.type === 'speakerConfirmation'}
        onClose={handleSpeakerConfirmationClose}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'background.default',
            },
          },
        }}
      >
        <DialogTitle>Update Speaker</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to update all segments with speaker "
            {modalState.data?.speakerConfirmation?.oldName}" to "
            {modalState.data?.speakerConfirmation?.newName}", or just this
            segment?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleConfirmSingleUpdate} variant="outlined">
            Just This Segment
          </Button>
          <Button
            onClick={handleConfirmAllUpdates}
            variant="contained"
            color="primary"
            autoFocus
          >
            All Segments
          </Button>
          <Button onClick={handleSpeakerConfirmationClose}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Add Vocabulary Dialog */}
      <Dialog
        open={modalState.type === 'addVocabulary'}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'background.default',
            },
          },
        }}
      >
        <DialogTitle>Add Word to Vocabulary</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Phrase (required)"
            fullWidth
            variant="outlined"
            value={vocabularyData.phrase}
            onChange={(e) =>
              setVocabularyData((prev) => ({
                ...prev,
                phrase: e.target.value,
              }))
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Sounds Like (optional)"
            fullWidth
            variant="outlined"
            value={vocabularyData.soundsLike}
            onChange={(e) =>
              setVocabularyData((prev) => ({
                ...prev,
                soundsLike: e.target.value,
              }))
            }
            helperText="How the word should be pronounced (e.g., am-ah-zon-ee-cee-too)"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="IPA (optional)"
            fullWidth
            variant="outlined"
            value={vocabularyData.ipa}
            disabled={true}
            onChange={(e) =>
              setVocabularyData((prev) => ({
                ...prev,
                ipa: e.target.value,
              }))
            }
            helperText="International Phonetic Alphabet notation"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Display As (optional)"
            fullWidth
            variant="outlined"
            value={vocabularyData.displayAs}
            onChange={(e) =>
              setVocabularyData((prev) => ({
                ...prev,
                displayAs: e.target.value,
              }))
            }
            helperText="How the word should appear in transcripts"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleOpenViewVocabulary}>
            View full vocabulary
          </Button>
          <Box>
            <Button onClick={handleCloseModal} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleVocabularySubmit}
              variant="contained"
              disabled={!vocabularyData.phrase || isSubmittingVocabulary}
            >
              {isSubmittingVocabulary ? 'Adding...' : 'Add to Vocabulary'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* View Vocabulary Dialog */}
      <Dialog
        open={modalState.type === 'viewVocabulary'}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              height: '80vh',
              maxHeight: '600px',
              backgroundColor: 'background.default',
            },
          },
        }}
      >
        <DialogTitle>Vocabulary List</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {isLoadingVocabulary ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : vocabularyError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {vocabularyError}
            </Alert>
          ) : vocabularyItems.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              p={4}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No vocabulary items found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add words to your vocabulary by clicking on highlighted words in
                the transcript
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: '100%', width: '100%' }}>
              <DataGrid
                rows={vocabularyItems.map((item, index) => ({
                  id: item.phrase + index,
                  phrase: item.phrase,
                  soundsLike: item.soundsLike || '',
                  ipa: item.ipa || '',
                  displayAs: item.displayAs || '',
                }))}
                columns={[
                  {
                    field: 'phrase',
                    headerName: 'Phrase',
                    flex: 1,
                    minWidth: 120,
                  },
                  {
                    field: 'soundsLike',
                    headerName: 'Sounds Like',
                    flex: 1,
                    minWidth: 120,
                    renderCell: (params) => params.value || '-',
                  },
                  {
                    field: 'ipa',
                    headerName: 'IPA',
                    flex: 1,
                    minWidth: 120,
                    renderCell: (params) => params.value || '-',
                  },
                  {
                    field: 'displayAs',
                    headerName: 'Display As',
                    flex: 1,
                    minWidth: 120,
                    renderCell: (params) => params.value || '-',
                  },
                  {
                    field: 'actions',
                    type: 'actions',
                    headerName: 'Actions',
                    width: 120,
                    getActions: (params) => [
                      <GridActionsCellItem
                        icon={
                          deletingItems.has(params.row.phrase) ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Delete />
                          )
                        }
                        label="Delete"
                        onClick={() =>
                          handleDeleteVocabulary(params.row.phrase)
                        }
                        disabled={deletingItems.has(params.row.phrase)}
                        sx={{ color: 'error.main' }}
                      />,
                    ],
                  },
                ]}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
